/**
 * Deterministic SVG renderers for the plot model.
 *
 * Renders a PlotModel to an SVG string (and a DOM helper). Output is a pure
 * function of the model, so it can be snapshot-tested without a browser. All
 * graphics use plain SVG - no charting library. Colors come from CSS custom
 * properties so graphs follow the light/dark theme.
 */

import type {
  PlotModel,
  NumberLineModel,
  CoordinateModel,
} from "./plot-model";

const STROKE = "currentColor";
const ACCENT = "var(--accent, #2f6df6)";

export function renderPlotSvg(model: PlotModel): string {
  return model.kind === "number-line"
    ? renderNumberLine(model)
    : renderCoordinate(model);
}

/** Create an SVG element node from a model (for insertion into the DOM). */
export function plotElement(model: PlotModel): SVGElement {
  const wrap = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  const tmp = document.createElement("div");
  tmp.innerHTML = renderPlotSvg(model);
  const svg = tmp.querySelector("svg");
  return (svg as SVGElement) ?? wrap;
}

// --- Number line ---------------------------------------------------------

const NL_W = 360;
const NL_H = 64;
const NL_PAD = 20;
const NL_AXIS_Y = 34;

function nlX(model: NumberLineModel, value: number): number {
  const span = model.max - model.min || 1;
  const usable = NL_W - 2 * NL_PAD;
  return NL_PAD + ((value - model.min) / span) * usable;
}

function renderNumberLine(model: NumberLineModel): string {
  const parts: string[] = [];
  // Axis line.
  parts.push(
    `<line x1="${NL_PAD}" y1="${NL_AXIS_Y}" x2="${NL_W - NL_PAD}" y2="${NL_AXIS_Y}" stroke="${STROKE}" stroke-width="2"/>`,
  );

  // Integer ticks + labels across the range.
  for (let t = Math.ceil(model.min); t <= Math.floor(model.max); t++) {
    const x = nlX(model, t);
    parts.push(
      `<line x1="${x.toFixed(1)}" y1="${NL_AXIS_Y - 5}" x2="${x.toFixed(1)}" y2="${NL_AXIS_Y + 5}" stroke="${STROKE}" stroke-width="1"/>`,
    );
    parts.push(
      `<text x="${x.toFixed(1)}" y="${NL_AXIS_Y + 20}" font-size="11" text-anchor="middle" fill="${STROKE}">${t}</text>`,
    );
  }

  // Shaded rays.
  for (const ray of model.rays) {
    const from = nlX(model, ray.from.toNumber());
    const to = ray.direction === "left" ? NL_PAD : NL_W - NL_PAD;
    parts.push(
      `<line x1="${from.toFixed(1)}" y1="${NL_AXIS_Y}" x2="${to.toFixed(1)}" y2="${NL_AXIS_Y}" stroke="${ACCENT}" stroke-width="4"/>`,
    );
    // Arrowhead at the open end.
    const dir = ray.direction === "left" ? -1 : 1;
    const tip = to;
    parts.push(
      `<path d="M ${(tip).toFixed(1)} ${NL_AXIS_Y} l ${(-dir * 8).toFixed(1)} -5 l 0 10 z" fill="${ACCENT}"/>`,
    );
    // Endpoint circle (open/closed).
    parts.push(endpointCircle(from, NL_AXIS_Y, ray.closed));
  }

  // Standalone points.
  for (const pt of model.points) {
    const x = nlX(model, pt.value.toNumber());
    parts.push(endpointCircle(x, NL_AXIS_Y, pt.closed));
  }

  return svgWrap(NL_W, NL_H, parts.join(""), "Number line");
}

function endpointCircle(cx: number, cy: number, closed: boolean): string {
  if (closed) {
    return `<circle cx="${cx.toFixed(1)}" cy="${cy}" r="6" fill="${ACCENT}"/>`;
  }
  return `<circle cx="${cx.toFixed(1)}" cy="${cy}" r="6" fill="var(--surface, #fff)" stroke="${ACCENT}" stroke-width="2.5"/>`;
}

// --- Coordinate plane ----------------------------------------------------

const CP = 280;
const CP_PAD = 24;

function cpX(model: CoordinateModel, x: number): number {
  const span = model.xMax - model.xMin || 1;
  return CP_PAD + ((x - model.xMin) / span) * (CP - 2 * CP_PAD);
}
function cpY(model: CoordinateModel, y: number): number {
  const span = model.yMax - model.yMin || 1;
  // SVG y grows downward; invert.
  return CP - CP_PAD - ((y - model.yMin) / span) * (CP - 2 * CP_PAD);
}

function renderCoordinate(model: CoordinateModel): string {
  const parts: string[] = [];

  // Grid lines at integers.
  for (let gx = Math.ceil(model.xMin); gx <= Math.floor(model.xMax); gx++) {
    const x = cpX(model, gx);
    parts.push(
      `<line x1="${x.toFixed(1)}" y1="${CP_PAD}" x2="${x.toFixed(1)}" y2="${CP - CP_PAD}" stroke="var(--border, #ddd)" stroke-width="0.5"/>`,
    );
  }
  for (let gy = Math.ceil(model.yMin); gy <= Math.floor(model.yMax); gy++) {
    const y = cpY(model, gy);
    parts.push(
      `<line x1="${CP_PAD}" y1="${y.toFixed(1)}" x2="${CP - CP_PAD}" y2="${y.toFixed(1)}" stroke="var(--border, #ddd)" stroke-width="0.5"/>`,
    );
  }

  // Axes (x=0, y=0) if in range.
  if (model.xMin <= 0 && model.xMax >= 0) {
    const x0 = cpX(model, 0);
    parts.push(
      `<line x1="${x0.toFixed(1)}" y1="${CP_PAD}" x2="${x0.toFixed(1)}" y2="${CP - CP_PAD}" stroke="${STROKE}" stroke-width="1.5"/>`,
    );
  }
  if (model.yMin <= 0 && model.yMax >= 0) {
    const y0 = cpY(model, 0);
    parts.push(
      `<line x1="${CP_PAD}" y1="${y0.toFixed(1)}" x2="${CP - CP_PAD}" y2="${y0.toFixed(1)}" stroke="${STROKE}" stroke-width="1.5"/>`,
    );
  }

  // Segments / lines.
  for (const seg of model.segments) {
    const x1 = cpX(model, seg.from.x.toNumber());
    const y1 = cpY(model, seg.from.y.toNumber());
    const x2 = cpX(model, seg.to.x.toNumber());
    const y2 = cpY(model, seg.to.y.toNumber());
    parts.push(
      `<line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}" stroke="${ACCENT}" stroke-width="2.5"/>`,
    );
  }

  // Points.
  for (const pt of model.points) {
    const x = cpX(model, pt.x.toNumber());
    const y = cpY(model, pt.y.toNumber());
    const closed = pt.closed !== false;
    parts.push(
      closed
        ? `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="4.5" fill="${ACCENT}"/>`
        : `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="4.5" fill="var(--surface, #fff)" stroke="${ACCENT}" stroke-width="2"/>`,
    );
  }

  return svgWrap(CP, CP, parts.join(""), "Coordinate graph");
}

function svgWrap(w: number, h: number, inner: string, label: string): string {
  return (
    `<svg viewBox="0 0 ${w} ${h}" width="100%" style="max-width:${w}px;height:auto" ` +
    `role="img" aria-label="${label}" xmlns="http://www.w3.org/2000/svg">${inner}</svg>`
  );
}
